import React, { useReducer } from 'react';
import CovidEpassTrains from '../route_info/components/CovidEpassTrains';
import { BreadCrumb, FooterSection, FaqSection } from '../components';
import InfoCard from '../components/InfoCard';
import { TrainClasses } from '../route_info/TrainClasses';
import { formatDuration, formatDuration2 } from '../../utils/dateTimeFormatUtils';
import DataLoaderError from '../../errors/DataLoaderError';
import { EnvironmentVariables, CovidSpecialTrainsPageData } from '../types';
import { getAppEnv } from '../../../config/env';
import { searchWidgetInitialState, searchWidgetReducer } from '../../reducers/searchWidgetReducer';
import SearchWidget from '../components/SearchWidget/SearchWidget';
import SEARCH_WIDGET_UTILS from '../components/SearchWidget/utils/utils';
import HtmlHeader from '../components/HtmlHeader';
import { FOOTER_DATA_IRCTC, IRCTC_EXTERNAL_JS } from '../../utils/RailsSEOConstants';
import { getStaticPageLoadEvent } from '../../utils/omnitureUtils';

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;
const SearchWidgetContext = React.createContext<any>(searchWidgetInitialState);

type FestiveSpecialTrainsPageDataProps = {
  data: CovidSpecialTrainsPageData;
};
function decorateListingData(listingData) {
  const { festiveSpecialTrains } = listingData;

  festiveSpecialTrains.forEach((train) => {
    // if the current format is in format say 5hr, make it 5hr 0mins
    if (String(train.duration).endsWith('hr')) {
      train.duration = train.duration.concat(' 0mins');
    }
    train.durationFormatted = formatDuration(train.duration);
    train.durationParts = formatDuration2(train.duration.replace('hr ', ':').replace('mins', ''));
  });
  return listingData;
}

const FestiveSpecialTrainsPage = ({ data }: FestiveSpecialTrainsPageDataProps) => {
  const [searchWidgetState, searchWidgetDispatch] = useReducer(searchWidgetReducer, searchWidgetInitialState);
  const { onTrainExtraSectionClicked } = SEARCH_WIDGET_UTILS.FESTIVE_SPECIAL_TRAINS(searchWidgetDispatch);
  return (
    <div>
      <SearchWidgetContext.Provider value={{ searchWidgetState, searchWidgetDispatch }}>
        <SearchWidget
          openDatePickerOnMount={false}
          topFix={true}
          classNode={TrainClasses.FC}
          pageWiseContext={SearchWidgetContext}
        />
      </SearchWidgetContext.Provider>

      <div className="container">
        <HtmlHeader data={data} />
        <BreadCrumb breadcrumb={data.template.breadcrumb} />

        <div className="heading">
          <h1 className="appendTop15 appendBottom15 font22">{data.template.h1_title}</h1>
        </div>
        <section>
          <CovidEpassTrains trainsList={data.festiveSpecialTrains} onClicked={onTrainExtraSectionClicked} />
        </section>
        <section>
          <h2 className="desktopShow appendBottom15">{`Information regarding Festive Special Trains`}</h2>
          <InfoCard infos={data.infos} />
        </section>
        <FaqSection faq={data.faq} pageUrl={data.template.sample_url} type="festiveSpecialTrains" />
      </div>
      <FooterSection template={data.template} />
    </div>
  );
};

FestiveSpecialTrainsPage.fetchData = async (_, match, urlObj) => {
  const url = `${DATA_LOADER_URL}/api/getFestiveSpecialTrainsData?refreshMode=static`;

  let responseData;
  const response = await fetch(url);
  // eslint-disable-next-line no-console
  console.info('url', url);
  // eslint-disable-next-line no-console
  console.info('response.status', url, response.status);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
    // console.log(responseData)
  } catch (e) {
    throw new Error('JSON parse exception: ' + textResponse);
  }
  // eslint-disable-next-line no-console
  console.log('responseData.status', responseData.status);
  if (responseData.status !== 'SUCCESS') {
    throw new DataLoaderError(`Unable to fetch for covid special trains list`, responseData.error);
  }
  responseData.faq = [
    {
      question: 'How many festive special Trains are running?',
      answer: `There are 392 festive special trains introduced by the by Indian Railways.`
    },
    {
      question: `How do I book confirm train ticket in these trains?`,
      answer: `Some trains are running daily, while others are running bi-weekly and tri-weekly. These special trains will carter to the cities like Kolkata, Delhi, Patna, Varanasi, Chennai, Jaipur, Lucknow, Kanpur, Bangalore, etc. Go to MMT website and app have the information on festive special train schedule, routes, timings, frequency and list of stations. You can check availability and train fare in 2AC, 3AC, AC Chair Car, Sleeper and Seating class trains. MakeMyTrip also shows availability from nearby stations and help you find confirm train ticket.`
    },
    {
      question: `How will be the fare of these trains?`,
      answer: `The fare for these festive special services will be similar to the applicable fare of special trains.`
    },
    {
      question: `How can I book a train ticket on the MakeMyTrip?`,
      answer: `1. Open the MakeMyTrip app or website and go to on the ‘Trains’ icon.\r\n<br/>\r\n
        2. Enter the source and destination stations and select your date of travel.\r\n<br/>\r\n
        3. Tap on the SEARCH button.\r\n<br/>\r\n
        4. Select your desired train and choose the class of travel, as per your convenience.\r\n<br/>\r\n
        5. Enter your IRCTC User ID, fill in the passenger details and proceed to the payment section.\r\n<br/>\r\n
        6. Select your preferred payment option and make the payment. Once the payment is done, re-enter your IRCTC password, decode the captcha and tap on the ‘Submit’ button.\r\n<br/>\r\n
        7. You will get your booked e-Ticket on both SMS and Email.`
    },
    {
      question: `What can you do to stay safe during train travel?`,
      answer: `1. Carry masks, wet wipes, gloves and a bottle of sanitizer for quick use.\r\n<br/>\r\n
        2. Keep your nose and mouse covered at all times.\r\n<br/>\r\n
        3. Clean you hands regularly.\r\n<br/>\r\n
        4.. We suggest you to teach the station well in advance, stay calm and help keep others (especially senior citizens and children around you) calm as well.\r\n<br/>\r\n
        5. Carry home cooked food and sufficient water for your journey.\r\n<br/>\r\n
        6. Download Arogya Setu app on a compatible mobile device.`
    },
    {
      question: `<div style="color: red;" >How many passengers can I add on a ticket while booking these trains?</div>`,
      answer: `You can add up to 6 adult passengers on a single ticket (if it is a general booking). On booking tatkal ticket in special trains, you can add up to 4 adult passengers.`
    }
  ];
  responseData.infos = [
    {
      query: `Railways to run Festive special trains to clear festive rush`,
      info: `Indian Railways has announced around 392 festive special trains to cater to the growing demand during the holiday period of Durga Puja, Dussehra, Diwali and Chhatt puja. These trains will be operational from 20th October 2020 and 30th November 2020. These festive trains will be operated at a speed of 55 kmph.`
    },
    {
      query: `Booking your train ticket:`,
      info: `Check MakeMyTrip’s website or mobile app for full detailed list of festive special trains, including train schedule, available dates and train route. Once you finalize train and date, you can proceed to the download MakeMyTrip mobile app or visit MakeMyTrip website to make your booking.`
    },
    {
      query: `Present Trains:`,
      info: `At present, more than 700 Covid Special and Clone Trains are operational. Due to pandemic, all regular train have been suspended indefinitely by the Indian Railways.`
    },
    {
      query: `Safety During Train Travel:`,
      info: `Railways has announced multiple safety measures to keep the passengers safe. For instance only online bookings are being allowed to avoid queues at stations, only asymptomatic passengers are allowed to board the train, to name a few. `
    }
  ];
  responseData.template = {
    // created: `Tue May 14 2019 11:08:50 GMT+0530 (India Standard Time)`,
    // modified: `Thu Jan 02 2020 06:16:07 GMT+0530 (India Standard Time)`,
    // id: `1`,
    // template_name: `route`,
    // type: `ab`,
    pageUrl: `https://www.makemytrip.com/railways/festive-special-trains.html`,
    meta_title: `Festival Special Trains, Book Festival Train 2020 By Indian Railways, IRCTC – MakeMyTrip`,
    meta_description: ` Planning to book festival special trains online. Book Chhath & Diwali train tickets online at MakeMytrip in advance without any hassle. Book your train tickets in advance ASAP.`,
    meta_keywords: `festival special train, diwali festival special train 2020, festival special train 2020`,
    analytics: getStaticPageLoadEvent('seo:rails:festive:trains'),
    footer_data: FOOTER_DATA_IRCTC,
    h1_title: `Book Festival Special Trains`,
    breadcrumb: [
      {
        redirectionUrl: `https://www.makemytrip.com/`,
        displayString: `Home`
      },
      {
        redirectionUrl: `https://www.makemytrip.com/railways/`,
        displayString: `Rails`
      },
      {
        displayString: `Festive Special Trains`,
        redirectionUrl: `https://www.makemytrip.com/railways/festive-special-trains.html`
      }
    ],
    banner_desktop: ``,
    banner_mobile: ``,
    sample_url: `https://www.makemytrip.com/railways/festive-special-trains.html`,
    // sub_title: `Following is the list of all the trains running between Delhi to Mumbai Railway Stations:`,
    // eslint-disable-next-line
    external_js: IRCTC_EXTERNAL_JS,
    extra_info: ``
  };
  let finalResponse;
  try {
    finalResponse = decorateListingData(responseData);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('err', e);
  }
  const pageType = 'trains';
  const cacheKey = 'HTML_STATIC_FESTIVE-SPECIAL-TRAINS.HTML';
  return {
    pushToCache: true, // if this is set then after ssr it will be stored in cache
    cacheKey,
    pageType,
    ...finalResponse
  };
};

export default FestiveSpecialTrainsPage;
